/* svnty-map-kit.js: the marks, the motion and the access layer every SVNTY map shares.
   The tracking page and the stores page import it; the map library is passed in, never assumed.
   ES2017, no dependencies, inline styles plus one small sheet (install it into the document or a shadow root).

   SvntyMapKit.tokens(variant)                         the palette ('paper' or 'night'), the same numbers as the style files
   SvntyMapKit.install(root)                           the sheet (keyframes, controls, credit) into document or a shadow root
   SvntyMapKit.fontsReady(ms)                          resolves when Instrument Sans 400/500/600 are ready (or after ms)
   SvntyMapKit.mapOptions(extra)                       the shared map options (flat camera, no fade, one finger scrolls the page)
   SvntyMapKit.flatten(map)                            no rotation, no pitch, from any input
   SvntyMapKit.branchPin(opts)                         the front logo as a pin: sizes s 32, m 44, l 60; states rest, near, selected
   SvntyMapKit.clusterMark(count, opts)                a ring holding an ink host with the count (the counted icon grammar)
   SvntyMapKit.userDot(opts)                           the viewer's own position: an ink dot on a paper ring, one pulse ring
   SvntyMapKit.courier(lib, map, lngLat, opts)         the lion (svnty-lion-marker.js) in the map's colours
   SvntyMapKit.area(map, id, center, meters, variant)  the destination district's soft area, edged with a stitch
   SvntyMapKit.quiet(map, names)                      hides the base map's own label for places our chips name
   SvntyMapKit.clusters(lib, map, items, opts)         screen space grouping of pins, synced to the camera
   SvntyMapKit.glide(marker, coords, opts)             moves a marker along a line at constant ground speed, eased
   SvntyMapKit.flyTo(map, lngLat, opts)                the flight to a branch; a short hop eases, reduced motion jumps
   SvntyMapKit.frame(map, lngLats, opts)               frames many points (the whole country, a district, a route)
   SvntyMapKit.branchZoom(item, items, pinPx)          the zoom at which a branch stands clear of its nearest neighbour
   SvntyMapKit.controls(map, opts)                     zoom in, zoom out and frame: one group, the button grammar
   SvntyMapKit.credit(host, html)                      the map credit: visible at first, then folds to an (i) button
   SvntyMapKit.label(map, opts)                        names the map region (opts.region: the box that also holds the controls
                                                       and the credit) and ties it to its text twin; say() speaks on moveend
   SvntyMapKit.health(map, fn)                         the tile watch: fn(false, info) when no tile of the base map loads,
                                                       fn(true) when tiles come back; the marks are drawn either way
   SvntyMapKit.floorZoom(map)                          the lowest zoom the camera can reach (minZoom or the maxBounds floor)
   SvntyMapKit.reduce() / setReduce(bool)              reduced motion: the OS setting (read live), or the page's own switch;
                                                       reaches the pulse and the lion already on the map
   MAP_STYLE_SVNTY, 2026-09-26. KIT_FIX, 2026-09-26: 1.1.0 */
(function (root) {
  'use strict';

  // ---------- tokens: build_styles.py writes this block from the one token table (svnty-map-tokens.json holds the same) ----------
  /* @tokens-begin: written by build_styles.py from the one token table; never edit by hand */
  var TOK = {"paper":{"variant":"paper","ground":"#FFFFFF","water":"#F2E8DF","waterLine":"#F2E8DF","valley":"#FBF8F5","urban":"#F6F6F6","building":"#F9F9F9","buildingEdge":"#E6E6E6","roadMotor":"#CCCCCC","roadMajor":"#DBDBDB","roadMid":"#E6E6E6","roadMinor":"#ECECEC","border":"#B2B2B2","label":"#616161","halo":"#FFFFFF","mark":"#000000","markOn":"#FFFFFF","route":"#000000","area":"#F2F2F2","areaEdge":"#B2B2B2","hairline":"rgba(0,0,0,0.12)","chip":"#FFFFFF","chipText":"#000000","focus":"#000000","ring":"#000000","success":"#185C3C","warning":"#8A5400","danger":"#9C2020"},"night":{"variant":"night","ground":"#132322","water":"#0A1313","waterLine":"#0A1313","valley":"#23312F","urban":"#1B2B2A","building":"#1A2A29","buildingEdge":"#2B3938","roadMotor":"#505C5B","roadMajor":"#3D4B4A","roadMid":"#32403F","roadMinor":"#2B3938","border":"#636E6D","label":"#BDC1C1","halo":"#132322","mark":"#FFFFFF","markOn":"#132322","route":"#FFFFFF","area":"#21302F","areaEdge":"#636E6D","hairline":"rgba(0,0,0,0.12)","chip":"#FFFFFF","chipText":"#000000","focus":"#FFFFFF","ring":"#BDC1C1","success":"#185C3C","warning":"#8A5400","danger":"#9C2020"}};
  var MOTION = {"ease":"cubic-bezier(.2,0,0,1)","easeVar":"--svnty-ease","fast":160,"base":240,"slow":400,"fastVar":"--svnty-t-fast","baseVar":"--svnty-t-base","slowVar":"--svnty-t-slow","pulseSlows":6};
  /* @tokens-end */
  function tokens(v) { return TOK[v === 'night' ? 'night' : 'paper']; }

  // ---------- the design system's one easing, cubic-bezier(.2,0,0,1), solved exactly ----------
  function bezier(x1, y1, x2, y2) {
    var cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx, cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    function sx(t) { return ((ax * t + bx) * t + cx) * t; }
    function sy(t) { return ((ay * t + by) * t + cy) * t; }
    function dx(t) { return (3 * ax * t + 2 * bx) * t + cx; }
    return function (x) {
      if (x <= 0) return 0; if (x >= 1) return 1;
      var t = x, i, d;
      for (i = 0; i < 8; i++) { d = sx(t) - x; if (Math.abs(d) < 1e-6) return sy(t); var s = dx(t); if (Math.abs(s) < 1e-6) break; t -= d / s; }
      var lo = 0, hi = 1; t = x;
      for (i = 0; i < 30; i++) { d = sx(t); if (Math.abs(d - x) < 1e-6) break; if (d < x) lo = t; else hi = t; t = (lo + hi) / 2; }
      return sy(t);
    };
  }
  var ease = bezier(0.2, 0, 0, 1);
  var T = { fast: MOTION.fast, base: MOTION.base, slow: MOTION.slow };   // the design system's durations, from the one table

  // ---------- reduced motion: the OS setting, unless the page says otherwise ----------
  // Every moving mark on a map subscribes, so a change (the OS setting, read live, or the page's own switch) reaches
  // the pulse and the lion that are already drawn, not only the next ones.
  var forced = null, subs = [], mql = null;
  try { mql = root.matchMedia('(prefers-reduced-motion: reduce)'); } catch (e) { mql = null; }
  function reduce() {
    if (forced !== null) return forced;
    return !!(mql && mql.matches);
  }
  function notify() { var on = reduce(); subs.slice().forEach(function (f) { try { f(on); } catch (e) { /* a removed mark */ } }); }
  function subscribe(f) { subs.push(f); f(reduce()); return function () { var k = subs.indexOf(f); if (k >= 0) subs.splice(k, 1); }; }
  if (mql) { if (mql.addEventListener) mql.addEventListener('change', notify); else if (mql.addListener) mql.addListener(notify); }
  function setReduce(on) { forced = on === null || on === undefined ? null : !!on; notify(); }

  // ---------- the sheet ----------
  var CSS = [
    '.svnty-mk{display:grid;place-items:center;width:44px;height:44px;padding:0;margin:0;border:0;background:none;cursor:pointer;-webkit-tap-highlight-color:transparent}',
    '.svnty-mk svg{display:block;overflow:visible}',
    '.svnty-mk--pin .svnty-mk__ring{opacity:0}',
    '.svnty-mk--pin[data-state="near"] .svnty-mk__ring,.svnty-mk--pin[data-state="selected"] .svnty-mk__ring{opacity:1}',
    '.svnty-mk--pin[data-state="selected"]{z-index:3}',
    '.svnty-mk--cluster{width:48px;height:48px}',
    '.svnty-mk--you{pointer-events:none;width:22px;height:22px}',
    '.svnty-mk--you:not(.maplibregl-marker){position:relative}',   /* on the map the library positions the root; off the map the pulse needs an anchor */
    /* THE PULSE (KIT_FIX P1-3): an SVG ring whose stroke never scales with the ring, so its line goes from 2 px to 0 on one
       keyframe pair, never snapped to a device pixel (a CSS border stalled at a third of a pixel on a 3x screen). NO FADE:
       nothing changes opacity; it widens while its line thins to nothing. Easing and duration are the design system's tokens
       by name (six slow steps), their values the fallbacks. The OS setting or the page's switch (data-reduce) stops it. */
    '.svnty-mk__pulse{position:absolute;left:0;top:0;width:22px;height:22px;overflow:visible;pointer-events:none}',
    '.svnty-mk__pulse circle{fill:none;stroke:currentColor;stroke-width:0;vector-effect:non-scaling-stroke;transform-box:view-box;transform-origin:11px 11px;transform:scale(1)}',
    '@media (prefers-reduced-motion: no-preference){.svnty-mk--you:not([data-still]):not([data-reduce]) .svnty-mk__pulse circle{animation:svnty-mk-pulse calc(var(' +
      MOTION.slowVar + ', ' + MOTION.slow + 'ms) * ' + MOTION.pulseSlows + ') var(' + MOTION.easeVar + ', ' + MOTION.ease + ') infinite}}',
    '@keyframes svnty-mk-pulse{from{transform:scale(1);stroke-width:2px}to{transform:scale(3);stroke-width:0}}',
    '.svnty-mapctl{display:flex;flex-direction:column;background:#FFFFFF;border:1px solid rgba(0,0,0,0.12);border-radius:6px;overflow:hidden}',
    '.svnty-mapctl button{display:grid;place-items:center;width:44px;height:44px;margin:0;padding:0;border:0;border-radius:0;background:#FFFFFF;color:#000000;cursor:pointer;-webkit-tap-highlight-color:transparent;position:relative}',
    '.svnty-mapctl button+button{border-top:1px solid rgba(0,0,0,0.12)}',
    '.svnty-mapctl button:hover{background:rgba(0,0,0,0.04)}',
    '.svnty-mapctl button:active{background:rgba(0,0,0,0.08)}',
    '.svnty-mapctl button:focus{outline:none}',
    /* the ring is concentric: inset 2 inside the radius 6 group gives radius 4 (KIT_FIX P2-7) */
    '.svnty-mapctl button:focus-visible::after{content:"";position:absolute;inset:2px;border:2px solid #000000;border-radius:4px}',
    /* at a limit the button stays in the tab order (aria-disabled, never disabled: focus never drops to the page, P1-5);
       its glyph takes the muted token, ink 62 % (P2-7) */
    '.svnty-mapctl button[aria-disabled="true"]{color:var(--svnty-muted, rgba(0,0,0,0.62));cursor:default;background:#FFFFFF}',
    '.svnty-mapctl svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}',
    '.svnty-credit{display:flex;align-items:flex-end;gap:6px;font:500 11px/1.4 "Instrument Sans",system-ui,sans-serif;color:#000000}',
    '.svnty-credit__text{background:#FFFFFF;border:1px solid rgba(0,0,0,0.12);border-radius:4px;padding:3px 6px;max-width:min(260px,calc(100vw - 96px))}',
    '.svnty-credit__text a{color:inherit;text-decoration:underline;text-underline-offset:2px}',
    '.svnty-credit[data-folded] .svnty-credit__text{display:none}',
    '.svnty-credit__i{display:grid;place-items:center;width:24px;height:24px;margin:0;padding:0;border:1px solid rgba(0,0,0,0.12);border-radius:4px;background:#FFFFFF;color:#000000;font:600 13px/1 "Instrument Sans",system-ui,sans-serif;cursor:pointer;position:relative}',
    '.svnty-credit__i::before{content:"";position:absolute;inset:-10px}',
    '.svnty-credit__i:focus{outline:none}',
    /* the ring sits on the map ground, so it takes the variant's ring token (ink on paper, the label token on night: P2-1) */
    '.svnty-credit__i:focus-visible{outline:2px solid var(--svnty-mk-ring, #000000);outline-offset:2px}',
    /* THE GESTURE HINT (KIT_FIX P1-2): no scrim over the map; a paper chip at the top that shows in one frame and hides in one
       frame after a 1.6 s hold (a visibility switch of zero duration: nothing interpolates, NO FADE) */
    '.maplibregl-map .maplibregl-cooperative-gesture-screen{inset:12px auto auto 50%;transform:translateX(-50%);width:max-content;max-width:calc(100% - 24px);box-sizing:border-box;display:block;padding:8px 12px;background:#FFFFFF;color:#000000;border:1px solid rgba(0,0,0,0.12);border-radius:4px;font:500 13px/1.35 "Instrument Sans",system-ui,sans-serif;text-align:center;opacity:1;visibility:hidden;transition:visibility 0s linear 1.6s;z-index:4}',
    '.maplibregl-map .maplibregl-cooperative-gesture-screen.maplibregl-show{opacity:1;visibility:visible;transition:none}',
    '.svnty-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}'
  ].join('\n');
  function install(where) {
    var host = where || document.head, doc = host.ownerDocument || document;
    if (host.querySelector && host.querySelector('style[data-svnty-map-kit]')) return;
    var s = doc.createElement('style'); s.setAttribute('data-svnty-map-kit', ''); s.textContent = CSS; host.appendChild(s);
  }

  // ---------- fonts: labels are drawn on the device, so the face must be ready before the map ----------
  function fontsReady(ms) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve(false);
    var all = Promise.all(['400', '500', '600'].map(function (w) { return document.fonts.load(w + ' 16px "Instrument Sans"'); }))
      .then(function () { return true; }, function () { return false; });
    return Promise.race([all, new Promise(function (r) { setTimeout(function () { r(false); }, ms || 1200); })]);
  }

  // ---------- the map's shared options ----------
  function mapOptions(extra) {
    var o = { attributionControl: false, fadeDuration: 0, cooperativeGestures: true, dragRotate: false, pitchWithRotate: false,
      maxPitch: 0, renderWorldCopies: false, touchPitch: false, boxZoom: false };
    for (var k in extra || {}) o[k] = extra[k];
    return o;
  }
  function flatten(map) {
    if (map.dragRotate) map.dragRotate.disable();
    if (map.touchZoomRotate) map.touchZoomRotate.disableRotation();
    if (map.keyboard && map.keyboard.disableRotation) map.keyboard.disableRotation();
    if (map.touchPitch) map.touchPitch.disable();
  }

  // ---------- helpers ----------
  var NS = 'http://www.w3.org/2000/svg';
  function svg(w, h, vb, inner) {
    var s = document.createElementNS(NS, 'svg'); s.setAttribute('width', w); s.setAttribute('height', h); s.setAttribute('viewBox', vb);
    s.setAttribute('aria-hidden', 'true'); s.setAttribute('focusable', 'false'); s.innerHTML = inner; return s;
  }
  function pinless(el) { el.setAttribute('aria-hidden', 'true'); el.tabIndex = -1; return el; }

  // ---------- the front logo (board 49: the black disc, the wordmark), the brand file's own paths ----------
  // Source: brand_assets/exports/svg/svnty_49-INV-WM-EMBLEM-disc-wordmark-1080.svg (disc centre 540,540, r 450).
  // Drawn in the board's own units and moved by (-90,-90), so the disc's box is 0 0 900 900. Never redrawn, never stretched.
  var WORDMARK = [
    'M296.056,547.151l-9.52-5.784c-5.972-3.646-10.22-7.233-12.746-10.762 c-2.527-3.528-3.79-7.588-3.79-12.182c0-6.89,2.39-12.484,7.172-16.786c4.78-4.3,10.993-6.452,18.633-6.452 c7.307,0,14.009,2.051,20.105,6.154v14.218c-6.305-6.064-13.09-9.097-20.356-9.097c-4.092,0-7.453,0.947-10.083,2.84 c-2.632,1.892-3.946,4.315-3.946,7.267c0,2.621,0.968,5.076,2.906,7.362c1.938,2.288,5.053,4.69,9.348,7.206l9.567,5.662 c10.673,6.375,16.009,14.49,16.009,24.343c0,7.015-2.348,12.714-7.046,17.099c-4.698,4.384-10.805,6.576-18.32,6.576 c-8.643,0-16.515-2.656-23.613-7.97v-15.908c6.764,8.571,14.593,12.855,23.487,12.855c3.924,0,7.192-1.092,9.803-3.276 c2.609-2.185,3.914-4.92,3.914-8.207C307.58,556.982,303.738,551.93,296.056,547.151z',
    'M427.036,496.314h12.475l-36.994,87.875h-2.838l-37.708-87.875h12.609l26.317,61.443L427.036,496.314z',
    'M557.099,496.314h11.9v87.499h-10.772l-58.499-67.388v67.388h-11.775v-87.499h10.146l59,67.96V496.314z',
    'M618.755,496.188h73.093v11.148h-30.503v76.476h-12.59v-76.476h-30.002L618.755,496.188L618.755,496.188z',
    'M794.794,496.314H810l-32.319,41.213v46.286h-12.841v-46.286l-32.318-41.213h15.122l23.417,30.165 L794.794,496.314z'
  ];

  // ---------- THE LION PIN (D9-12, 2026-09-26: the branch pins wear the same black lion as the tracking map) ----------
  // The brand file's own path (620 x 547.2), the white knockout under the ink so the mark stays clean over roads and labels.
  var LION_D = 'M590.68 0 567.28 0 496.89 58.66 516.84 104ZM501.35 110.46 480.69 63.49 327.34 28.17 396.88 95.44ZM310 179.23 381.22 103.62 310 35.02 238.78 103.62ZM310 203.63 309.97 203.66 236.92 126.11 254.29 387.88 310 445.71 365.71 387.88 383.08 126.11 310.03 203.66ZM223.12 95.44 292.66 28.17 139.31 63.49 118.65 110.46ZM123.11 58.66 52.71 0 29.32 0 103.16 104ZM15.28 9.19 0 48.74 39.06 168.2 93.7 119.63ZM219.24 112.94 108.58 128.85 43.45 186.74 43.45 274.7 122.03 394.22 181.9 440.11 181.9 388.6 233.27 324.36ZM310.14 543.89 310 543.8 309.86 543.89 268.96 519.28 239.35 519.28 280.95 547.2 310 547.2 339.05 547.2 380.65 519.28 351.04 519.28ZM332.5 511.13 310 497.86 287.5 511.13 310 524.17ZM382.03 395.2 310.06 468.84 310 468.78 309.94 468.84 237.97 395.2 234.91 349.16 198.67 394.48 198.67 455.48 217.48 502.51 269.1 502.51 310 478.39 350.9 502.51 402.52 502.51 421.33 455.48 421.33 394.48 385.09 349.16ZM400.76 112.94 386.74 324.36 438.1 388.6 438.1 440.11 497.97 394.22 576.55 274.7 576.55 186.74 511.42 128.85ZM604.72 9.19 526.3 119.63 580.94 168.2 620 48.74Z';
  var LION_W = 620, LION_H = 547.2;
  var PIN = { s: 28, m: 36, l: 48 };   // the lion's width per size
  var GAP = 3, RING = 1.5;
  function pinSize(size) { size = size in PIN ? size : 'm'; return size === 's' && (root.devicePixelRatio || 1) < 2 ? 'm' : size; }
  function lionPaths(x, y, w, ink, paper) {
    var k = w / LION_W;
    return '<g transform="translate(' + x + ' ' + y + ') scale(' + k + ')">' +
      '<path d="' + LION_D + '" fill="' + paper + '" stroke="' + paper + '" stroke-width="22" stroke-linejoin="round"/>' +
      '<path d="' + LION_D + '" fill="' + ink + '"/></g>';
  }
  function branchPin(opts) {
    opts = opts || {};
    var t = tokens(opts.variant), size = pinSize(opts.size), d = PIN[size], h = d * LION_H / LION_W;
    var box = Math.max(44, d + 2 * (GAP + RING) + 4), c = box / 2;
    var el = document.createElement('div');
    el.className = 'svnty-mk svnty-mk--pin'; el.setAttribute('data-size', size); el.setAttribute('data-state', opts.state || 'rest');
    el.style.width = el.style.height = box + 'px';
    if (opts.id) el.setAttribute('data-id', opts.id);
    el.appendChild(svg(box, box, '0 0 ' + box + ' ' + box,
      '<circle class="svnty-mk__ring" cx="' + c + '" cy="' + c + '" r="' + (d / 2 + GAP + RING / 2) + '" fill="none" stroke="' + t.mark + '" stroke-width="' + RING + '"/>' +
      lionPaths(c - d / 2, c - h / 2, d, t.mark, t.markOn)));
    pinless(el);
    return {
      el: el, size: size,
      state: function (s) { el.setAttribute('data-state', s); return this; },
      resize: function (s) { var n = branchPin({ variant: opts.variant, size: s, state: el.getAttribute('data-state'), id: opts.id });
        el.innerHTML = ''; el.appendChild(n.el.firstChild); el.style.width = el.style.height = n.el.style.width; el.setAttribute('data-size', n.size); return this; }
    };
  }

  // ---------- the cluster: the lion carrying its count (THE COUNTED ICON grammar: the badge at the top right) ----------
  function clusterMark(count, opts) {
    opts = opts || {};
    var t = tokens(opts.variant), box = 48, d = 34, h = d * LION_H / LION_W, n = String(count > 99 ? '99+' : count);
    var el = document.createElement('div'); el.className = 'svnty-mk svnty-mk--cluster'; el.setAttribute('data-count', n);
    el.style.width = el.style.height = box + 'px';
    var fs = n.length > 2 ? 9 : 11, bx = box - 11, by = 11, br = n.length > 2 ? 11 : 9.5;
    el.appendChild(svg(box, box, '0 0 ' + box + ' ' + box,
      lionPaths((box - d) / 2, (box - h) / 2 + 2, d, t.mark, t.markOn) +
      '<circle cx="' + bx + '" cy="' + by + '" r="' + (br + 1.5) + '" fill="' + t.markOn + '"/>' +
      '<circle cx="' + bx + '" cy="' + by + '" r="' + br + '" fill="' + t.mark + '"/>' +
      '<text x="' + bx + '" y="' + by + '" dy="0.36em" text-anchor="middle" fill="' + t.markOn + '" style="font:600 ' + fs + "px/1 'Instrument Sans',system-ui,sans-serif;font-variant-numeric:tabular-nums;letter-spacing:0\">" + n + '</text>'));
    return pinless(el);
  }

  // ---------- the viewer's own position ----------
  function userDot(opts) {
    opts = opts || {};
    var t = tokens(opts.variant), el = document.createElement('div');
    el.className = 'svnty-mk svnty-mk--you'; el.style.color = t.mark;
    if (opts.still) el.setAttribute('data-still', '');
    var p = svg(22, 22, '0 0 22 22', '<circle cx="11" cy="11" r="10"/>'); p.setAttribute('class', 'svnty-mk__pulse'); el.appendChild(p);
    el.appendChild(svg(22, 22, '0 0 22 22', '<circle cx="11" cy="11" r="11" fill="' + t.markOn + '"/><circle cx="11" cy="11" r="8" fill="' + t.mark + '"/>'));
    // the page's own switch and the OS setting reach a dot already on the map; a dot that left the page unsubscribes
    var seen = false, off = subscribe(function (on) {
      if (el.isConnected) seen = true; else if (seen) { off(); return; }
      if (on) el.setAttribute('data-reduce', ''); else el.removeAttribute('data-reduce');
    });
    return pinless(el);
  }

  // ---------- the courier: the lion, in the map's colours ----------
  function courier(lib, map, lngLat, opts) {
    if (!root.SvntyLion) throw new Error('svnty-lion-marker.js must load before the map kit courier');
    var t = tokens(opts && opts.variant), o = { color: t.mark, paper: t.markOn, reduceMotion: reduce() };
    for (var k in opts || {}) if (k !== 'variant') o[k] = opts[k];
    var h = root.SvntyLion.attach(lib, map, lngLat, o), first = true;
    // the switch reaches the lion on the map (P2-6); a page that forced reduceMotion itself keeps that choice
    var off = opts && opts.reduceMotion != null ? function () {} : subscribe(function (on) { if (first) { first = false; return; } if (h.lion && h.lion.reduceMotion) h.lion.reduceMotion(on); });
    var destroy = h.destroy; h.destroy = function () { off(); return destroy.apply(h, arguments); };
    return h;
  }

  // ---------- geometry ----------
  var R = 6371008.8, RAD = Math.PI / 180;
  function meters(a, b) {
    var x = (b[0] - a[0]) * RAD * Math.cos((a[1] + b[1]) / 2 * RAD), y = (b[1] - a[1]) * RAD; return Math.sqrt(x * x + y * y) * R;
  }
  function circle(center, m, n) {
    var pts = [], i, lat = center[1] * RAD, dy = m / R / RAD, dx = dy / Math.cos(lat);
    for (i = 0; i <= (n || 72); i++) { var a = i / (n || 72) * 2 * Math.PI; pts.push([center[0] + dx * Math.cos(a), center[1] + dy * Math.sin(a)]); }
    return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [pts] } };
  }
  function ll(p) { return Array.isArray(p) ? p : [p.lng, p.lat]; }

  // ---------- the destination district's soft area: a quiet fill, edged with a stitch ----------
  function area(map, id, center, m, variant, beforeId) {  // m in meters (default 1000)
    var t = tokens(variant), src = id + '-area';
    map.addSource(src, { type: 'geojson', data: circle(ll(center), m || 1000) });
    map.addLayer({ id: src + '-fill', type: 'fill', source: src, paint: { 'fill-color': t.area } }, beforeId);
    map.addLayer({ id: src + '-stitch', type: 'line', source: src, layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: { 'line-color': t.areaEdge, 'line-width': 1, 'line-dasharray': [3, 2.5] } }, beforeId);
    return {
      set: function (c, mm) { map.getSource(src).setData(circle(ll(c), mm || m || 1000)); },
      remove: function () { [src + '-stitch', src + '-fill'].forEach(function (l) { if (map.getLayer(l)) map.removeLayer(l); }); if (map.getSource(src)) map.removeSource(src); }
    };
  }

  // ---------- quiet: the base map stops printing places our own chips already name ----------
  function quiet(map, names) {
    ['label-city', 'label-district'].forEach(function (id) {
      var L = map.getLayer(id); if (!L) return;
      // the name the layer prints, exactly (the style's own text field, name:latin included: P2-10)
      var f = map.getFilter(id), tf = map.getLayoutProperty(id, 'text-field');
      var nm = Array.isArray(tf) ? tf : ['coalesce', ['get', 'name:en'], ['get', 'name_en'], ['get', 'name:latin'], ['get', 'name']];
      map.setFilter(id, ['all', f || true, ['!', ['in', nm, ['literal', names]]]]);
    });
  }

  // ---------- THE PLACES WE NAME (D9-13, 2026-09-26): Gaza and Palestine on every SVNTY map ----------
  // The style prints Egypt's own cities; these two names are the kit's own labels in the same type, drawn over the base labels.
  var PLACES = { type: 'FeatureCollection', features: [
    { type: 'Feature', properties: { name: 'Gaza', kind: 'city' }, geometry: { type: 'Point', coordinates: [34.4667, 31.5017] } },
    { type: 'Feature', properties: { name: 'Palestine', kind: 'country' }, geometry: { type: 'Point', coordinates: [35.2, 31.95] } }
  ] };
  function places(map, variant) {
    if (!map || map.getSource('svnty-places')) return;
    var night = variant === 'night';
    map.addSource('svnty-places', { type: 'geojson', data: PLACES });
    map.addLayer({
      id: 'svnty-places-label', type: 'symbol', source: 'svnty-places', minzoom: 3.5,
      layout: {
        'text-field': ['get', 'name'], 'text-font': ['Instrument Sans Medium', 'Instrument Sans'], 'text-transform': 'uppercase',
        'text-letter-spacing': ['case', ['==', ['get', 'kind'], 'country'], 0.16, 0.08],
        'text-size': ['interpolate', ['linear'], ['zoom'], 4, ['case', ['==', ['get', 'kind'], 'country'], 13, 12], 12, ['case', ['==', ['get', 'kind'], 'country'], 14, 13]],
        'text-max-width': 9, 'text-padding': 8, 'text-allow-overlap': false
      },
      paint: { 'text-color': night ? '#BDC1C1' : '#616161', 'text-halo-color': night ? '#132322' : '#FFFFFF', 'text-halo-width': 2 }
    });
  }

  // ---------- zoom at which a branch stands clear of its nearest neighbour ----------
  function branchZoom(item, items, pinPx) {
    var p = ll(item.lngLat), best = Infinity;
    (items || []).forEach(function (o) { if (o !== item && o.id !== item.id) best = Math.min(best, meters(p, ll(o.lngLat))); });
    if (!isFinite(best)) return 15;
    var need = (pinPx || 44) + 12, z = Math.log(156543.034 * Math.cos(p[1] * RAD) * need / best) / Math.LN2 + 0.25;
    return Math.max(15, Math.min(17, z));
  }

  // ---------- the clusters: greedy grouping in screen space, redrawn only when the grouping changes ----------
  function clusters(lib, map, items, opts) {
    opts = opts || {};
    var radius = opts.radius || 46, variant = opts.variant, pins = {}, groups = {}, key = '', selected = null, near = null;
    items.forEach(function (it) {
      var p = branchPin({ variant: variant, size: opts.size || 'm', id: it.id });
      p.el.addEventListener('click', function (e) { e.stopPropagation(); if (opts.onPick) opts.onPick(it); });
      pins[it.id] = { item: it, pin: p, marker: new lib.Marker({ element: p.el, anchor: 'center', subpixelPositioning: true }).setLngLat(ll(it.lngLat)), on: false };
    });
    function compute() {
      var pts = items.map(function (it) { var q = map.project(ll(it.lngLat)); return { it: it, x: q.x, y: q.y }; }), out = [], used = {};
      pts.sort(function (a, b) { return (b.it.id === selected) - (a.it.id === selected); });   // the selected branch never hides in a group
      pts.forEach(function (a) {
        if (used[a.it.id]) return; var g = [a]; used[a.it.id] = 1;
        if (a.it.id !== selected) pts.forEach(function (b) { if (!used[b.it.id] && b.it.id !== selected && Math.hypot(a.x - b.x, a.y - b.y) < radius) { g.push(b); used[b.it.id] = 1; } });
        out.push(g.map(function (x) { return x.it; }));
      });
      return out;
    }
    function draw() {
      var gs = compute(), k = gs.map(function (g) { return g.map(function (i) { return i.id; }).sort().join('+'); }).sort().join('|');
      if (k === key) return; key = k;
      var live = {};
      gs.forEach(function (g) {
        if (g.length === 1) { var P = pins[g[0].id]; live['p:' + g[0].id] = 1; if (!P.on) { P.marker.addTo(map); P.on = true; } return; }
        var gk = 'g:' + g.map(function (i) { return i.id; }).sort().join('+'); live[gk] = 1;
        if (!groups[gk]) {
          var c = [0, 0]; g.forEach(function (i) { var q = ll(i.lngLat); c[0] += q[0] / g.length; c[1] += q[1] / g.length; });
          var el = clusterMark(g.length, { variant: variant });
          el.addEventListener('click', function (e) { e.stopPropagation(); frame(map, g.map(function (i) { return i.lngLat; }), { padding: opts.padding || 64,
            maxZoom: Math.min(17, Math.max.apply(null, g.map(function (i) { return branchZoom(i, g, 44); }))) }); if (opts.onGroup) opts.onGroup(g); });
          groups[gk] = new lib.Marker({ element: el, anchor: 'center', subpixelPositioning: true }).setLngLat(c).addTo(map);
        }
      });
      Object.keys(pins).forEach(function (id) { var P = pins[id]; if (!live['p:' + id] && P.on) { P.marker.remove(); P.on = false; } });
      Object.keys(groups).forEach(function (gk) { if (!live[gk]) { groups[gk].remove(); delete groups[gk]; } });
      if (opts.onChange) opts.onChange(gs);
    }
    map.on('zoom', draw); map.on('moveend', draw); map.on('resize', draw);
    draw();
    return {
      select: function (id) {
        if (selected && pins[selected]) pins[selected].pin.resize(opts.size || 'm').state('rest');
        selected = id || null; key = '';
        if (selected && pins[selected]) pins[selected].pin.resize('l').state('selected');
        draw();
      },
      near: function (id) { if (near && pins[near] && near !== selected) pins[near].pin.state('rest'); near = id || null; if (near && pins[near] && near !== selected) pins[near].pin.state('near'); },
      refresh: function () { key = ''; draw(); },
      destroy: function () { map.off('zoom', draw); map.off('moveend', draw); map.off('resize', draw);
        Object.keys(pins).forEach(function (id) { pins[id].marker.remove(); }); Object.keys(groups).forEach(function (g) { groups[g].remove(); }); }
    };
  }

  // ---------- the glide: constant ground speed along the line, the design system's easing on the whole trip ----------
  function glide(marker, coords, opts) {
    opts = opts || {};
    var pts = coords.map(ll), cum = [0], i;
    for (i = 1; i < pts.length; i++) cum[i] = cum[i - 1] + meters(pts[i - 1], pts[i]);
    var total = cum[cum.length - 1] || 1, ms = opts.ms || 5200, e = opts.easing || ease, raf = 0, stopped = false, resolve;
    var done = new Promise(function (r) { resolve = r; });
    function at(u) {
      var d = u * total, lo = 0, hi = cum.length - 1;
      while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (cum[mid] < d) lo = mid; else hi = mid; }
      var seg = cum[hi] - cum[lo] || 1, f = (d - cum[lo]) / seg;
      return [pts[lo][0] + (pts[hi][0] - pts[lo][0]) * f, pts[lo][1] + (pts[hi][1] - pts[lo][1]) * f];
    }
    function end() { marker.setLngLat(pts[pts.length - 1]); if (opts.onFrame) opts.onFrame(pts[pts.length - 1], 1); if (opts.onDone) opts.onDone(); resolve(true); }
    if (reduce() || pts.length < 2) { end(); return { cancel: function () {}, done: done, at: at }; }
    var t0 = null;
    function step(ts) {
      if (stopped) return; if (t0 === null) t0 = ts;
      var u = Math.min(1, (ts - t0) / ms), p = at(e(u));
      marker.setLngLat(p); if (opts.onFrame) opts.onFrame(p, u);
      if (u < 1) raf = requestAnimationFrame(step); else end();
    }
    raf = requestAnimationFrame(step);
    return { cancel: function () { stopped = true; cancelAnimationFrame(raf); resolve(false); }, done: done, at: at };
  }

  // ---------- the camera ----------
  // THE MAXDURATION TRAP: the library does not clamp a flight to maxDuration, it cancels the animation and jumps
  // (duration 0) whenever the natural duration is longer. So the kit never passes maxDuration; it sets the duration
  // itself from the zoom change: 700 ms plus 150 ms per zoom level, held between 800 and 2200 ms.
  var FLY = { curve: 1.42, base: 700, perZoom: 150, min: 800, max: 2200 };
  function flightMs(dz) { return Math.round(Math.min(FLY.max, Math.max(FLY.min, FLY.base + FLY.perZoom * Math.abs(dz)))); }
  function flyTo(map, lngLat, opts) {
    opts = opts || {};
    var c = ll(lngLat), z = opts.zoom != null ? opts.zoom : 15, off = opts.offset || [0, 0];
    // reduced motion: an instant move that honors the offset (jumpTo has no offset, it landed 120 px off: P1-4)
    if (reduce()) { map.easeTo({ center: c, zoom: z, offset: off, duration: 0 }); return; }
    var px = map.project(c), cv = map.getCanvas(), w = cv.clientWidth, h = cv.clientHeight, dz = z - map.getZoom();
    var inView = px.x > 0 && px.y > 0 && px.x < w && px.y < h;
    if (inView && Math.abs(dz) <= 1.5) { map.easeTo({ center: c, zoom: z, offset: off, duration: T.slow, easing: ease }); return; }
    map.flyTo({ center: c, zoom: z, offset: off, curve: FLY.curve, duration: flightMs(dz), easing: ease, essential: true });
  }
  function frame(map, lngLats, opts) {
    opts = opts || {};
    var pts = lngLats.map(ll);
    if (!pts.length) return;
    if (pts.length === 1) { flyTo(map, pts[0], { zoom: opts.maxZoom || 15 }); return; }
    var b = pts.reduce(function (a, p) { return [[Math.min(a[0][0], p[0]), Math.min(a[0][1], p[1])], [Math.max(a[1][0], p[0]), Math.max(a[1][1], p[1])]]; }, [[180, 90], [-180, -90]]);
    var o = { padding: opts.padding != null ? opts.padding : 56, maxZoom: opts.maxZoom || 16 };
    var cam = map.cameraForBounds(b, o);
    if (!cam) return;
    if (reduce()) { map.jumpTo(cam); return; }
    map.flyTo({ center: cam.center, zoom: cam.zoom, curve: FLY.curve, duration: flightMs(cam.zoom - map.getZoom()), easing: ease, essential: true });
  }

  // ---------- the floor: the lowest zoom the camera can reach ----------
  // The library holds the camera so the maxBounds box covers the viewport: at zoom z the world is 512 * 2^z px wide, so the
  // box's Mercator span times that must reach the container's width and height.
  function mercY(lat) { return (180 - 180 / Math.PI * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360))) / 360; }
  function floorZoom(map) {
    var z = map.getMinZoom(), b = map.getMaxBounds && map.getMaxBounds();
    if (!b) return z;
    var box = map.getContainer(), w = box.clientWidth, h = box.clientHeight;
    var dx = (b.getEast() - b.getWest()) / 360, dy = mercY(b.getSouth()) - mercY(b.getNorth());
    if (!(w > 0 && h > 0 && dx > 0 && dy > 0)) return z;
    return Math.max(z, Math.log(w / (dx * 512)) / Math.LN2, Math.log(h / (dy * 512)) / Math.LN2);
  }

  // ---------- the controls: one group, three buttons, the button grammar ----------
  var GLYPH = { 'in': '', out: '', frame: '' };   /* D11-3 THE ICON LAW: no path of its own; the family's glyph comes from the page's template (svnty-glyphs) */
  function controls(map, opts) {
    opts = opts || {};
    var L = opts.labels || { 'in': 'Zoom in', out: 'Zoom out', frame: opts.frameLabel || 'Show all' };
    var g = document.createElement('div'); g.className = 'svnty-mapctl'; g.setAttribute('role', 'group'); g.setAttribute('aria-label', opts.groupLabel || 'Map zoom');
    // THE ICON LAW (D9-9): the family's glyph cloned from the page's <template data-svnty-icons> (plus, minus, frame), so the
    // zoom group wears the same plus, minus and frame as every other surface; the inline paths stand in only without the template
    var FAMILY = { 'in': 'plus', out: 'minus', frame: 'frame' };
    function glyph(kind) {
      var tpl = document.querySelector('template[data-svnty-icons]');
      var src = tpl && tpl.content ? tpl.content.querySelector('[data-icon="' + FAMILY[kind] + '"] svg') : null;
      if (src) { var c = src.cloneNode(true); c.setAttribute('width', 20); c.setAttribute('height', 20); c.setAttribute('aria-hidden', 'true'); c.setAttribute('focusable', 'false'); return c; }
      return svg(20, 20, '0 0 24 24', GLYPH[kind]);
    }
    function b(kind, fn) {
      var x = document.createElement('button'); x.type = 'button'; x.setAttribute('aria-label', L[kind]);
      x.appendChild(glyph(kind)); x.addEventListener('click', fn); g.appendChild(x); return x;
    }
    function off(x) { return x.getAttribute('aria-disabled') === 'true'; }
    // D9-12b: a zoom in from the whole-country view used to zoom about Egypt's centre and lose every branch off the top;
    // the page may hand the group an `around` function (the centre of the marks in view) and the step zooms about it
    var bi = b('in', function () {
      if (off(bi)) return;
      var a = null; try { a = opts.around && opts.around(); } catch (e) { a = null; }
      if (a) map.easeTo({ center: a, zoom: Math.min(map.getMaxZoom(), map.getZoom() + 1), duration: reduce() ? 0 : T.base, easing: ease });
      else map.zoomIn({ duration: reduce() ? 0 : T.base, easing: ease });
    });
    var bo = b('out', function () { if (!off(bo)) map.zoomOut({ duration: reduce() ? 0 : T.base, easing: ease }); });
    if (opts.onFrame) b('frame', opts.onFrame);
    // aria-disabled, never disabled: a disabled button drops keyboard focus to the page (P1-5). The floor is the one the
    // camera can reach: maxBounds can hold it above minZoom (4.77 against 4 on the proof: P1-6).
    function set(x, on) { if (on) x.setAttribute('aria-disabled', 'true'); else x.removeAttribute('aria-disabled'); }
    function sync() { var z = map.getZoom(); set(bi, z >= map.getMaxZoom() - 0.01); set(bo, z <= floorZoom(map) + 0.01); }
    map.on('zoomend', sync); map.on('moveend', sync); map.on('resize', sync); map.on('load', sync); sync();
    return g;
  }

  // ---------- the credit: visible at first (the data licence asks for it), folds to (i) after 5 s or the first touch ----------
  // D11-4 THE CLEAN MAP: ONE credit string for every SVNTY map; the (i) starts folded when opts.folded is true (never a visible line)
  var CREDIT = 'Map <a href="https://openfreemap.org">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/">OpenMapTiles</a> &middot; Data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &middot; <a href="/policies/terms-of-service">Licenses</a>';
  function credit(host, html, opts) {
    opts = opts || {};
    html = html || CREDIT;
    var w = document.createElement('div'); w.className = 'svnty-credit';
    // the focus ring of the (i) sits on the map ground: the variant's ring token (the style's metadata names the variant)
    function paint(v) { w.style.setProperty('--svnty-mk-ring', tokens(v).ring); }
    paint(opts.variant || (opts.map && variantOf(opts.map)));
    if (!opts.variant && opts.map) opts.map.on('styledata', function () { paint(variantOf(opts.map)); });
    // html is the style's own attribution string (links to the data licences), written by us, never by a visitor
    var t = document.createElement('div'); t.className = 'svnty-credit__text'; t.id = 'svnty-credit-' + Math.random().toString(36).slice(2, 8); t.innerHTML = html;
    var i = document.createElement('button'); i.type = 'button'; i.className = 'svnty-credit__i'; i.textContent = 'i';
    i.setAttribute('aria-label', opts.label || 'Map data credits'); i.setAttribute('aria-controls', t.id); i.setAttribute('aria-expanded', 'true');
    w.appendChild(t); w.appendChild(i); host.appendChild(w);
    if (opts.folded) { w.setAttribute('data-folded', ''); i.setAttribute('aria-expanded', 'false'); }
    function fold(on) { if (on) w.setAttribute('data-folded', ''); else w.removeAttribute('data-folded'); i.setAttribute('aria-expanded', on ? 'false' : 'true'); }
    i.addEventListener('click', function () { fold(!w.hasAttribute('data-folded')); });
    var timer = opts.folded ? 0 : setTimeout(function () { fold(true); }, opts.ms || 5000);
    // only a person's own pan or zoom folds it early; the page's own camera moves do not
    if (opts.map) opts.map.on('movestart', function h(e) { if (!e || !e.originalEvent) return; opts.map.off('movestart', h); clearTimeout(timer); fold(true); });
    w.querySelectorAll('a').forEach(function (a) { if (/^https?:/.test(a.getAttribute('href') || '')) { a.target = '_blank'; a.rel = 'noopener noreferrer'; } });
    return { el: w, fold: fold };
  }

  function variantOf(map) {
    try { var st = map.getStyle(); return st && st.metadata && st.metadata['svnty:variant']; } catch (e) { return undefined; }
  }

  // ---------- the access layer ----------
  // ONE named region (P2-5): opts.region is the box that holds the canvas, the zoom group and the credit (default: the map's
  // own container). The library gives its canvas role region too, a region inside the region: the canvas becomes the
  // application the arrow keys drive, described as a map, so the reader meets one landmark.
  function label(map, opts) {
    opts = opts || {};
    var box = opts.region || map.getContainer(), cv = map.getCanvas(), ct = map.getContainer();
    if (box !== ct && ct.getAttribute('role') === 'region') ct.removeAttribute('role');
    box.setAttribute('role', 'region'); box.setAttribute('aria-label', opts.label || 'Map');
    if (opts.describedBy) box.setAttribute('aria-describedby', opts.describedBy);
    cv.setAttribute('role', 'application');
    cv.setAttribute('aria-label', opts.canvasLabel || 'Map. Arrow keys move it, plus and minus zoom.');
    cv.setAttribute('aria-roledescription', 'map');
    var live = document.createElement('div'); live.className = 'svnty-sr'; live.setAttribute('aria-live', 'polite'); box.appendChild(live);
    // P2-12: the map speaks what it shows once the camera lands (moveend), never while it still flies; the last word wins,
    // and a camera that never settles is not waited on past 4 s
    var pending = null, timer = 0;
    function speak() { if (pending === null) return; var s = pending; pending = null; clearTimeout(timer); map.off('moveend', speak);
      live.textContent = ''; setTimeout(function () { live.textContent = s; }, 60); }
    return { say: function (s) {
      pending = s; map.off('moveend', speak); clearTimeout(timer);
      if (map.isMoving && map.isMoving()) { map.once('moveend', speak); timer = setTimeout(speak, 4000); } else speak();
    } };
  }

  // ---------- the tile watch: a blank field is never silent (P2-2) ----------
  // fn(false, {errors}) when the map settled (idle, or 12 s after the first error) with errors and not one tile loaded;
  // fn(true) once tiles load after that. The kit draws its marks either way: the pins stay on the field.
  function health(map, fn, opts) {
    opts = opts || {};
    var errors = 0, tiles = 0, down = false, timer = 0;
    function judge() {
      clearTimeout(timer);
      if (!down && errors > 0 && tiles === 0) { down = true; fn(false, { errors: errors }); }
    }
    map.on('error', function () { errors++; if (!timer && !down) timer = setTimeout(judge, opts.ms || 12000); });
    // only the base map's tiles count: a page's own GeoJSON layers (the route, the district) load even when the tile host is down
    function base(e) { var src = e && e.sourceId && map.getSource(e.sourceId); return !src || src.type !== 'geojson'; }
    map.on('data', function (e) {
      if (e && e.dataType === 'source' && e.tile && base(e)) { tiles++; if (down) { down = false; fn(true, { errors: errors }); } }
    });
    map.on('idle', judge);
    return { down: function () { return down; } };
  }

  root.SvntyMapKit = {
    version: '1.2.0', tokens: tokens, motion: function () { return MOTION; }, install: install, css: function () { return CSS; }, fontsReady: fontsReady,
    mapOptions: mapOptions, flatten: flatten, branchPin: branchPin, clusterMark: clusterMark, userDot: userDot, courier: courier, CREDIT: CREDIT,
    area: area, quiet: quiet, clusters: clusters, places: places, glide: glide, flyTo: flyTo, frame: frame, branchZoom: branchZoom, controls: controls,
    credit: credit, label: label, health: health, floorZoom: floorZoom, flightMs: flightMs, reduce: reduce, setReduce: setReduce, ease: ease, bezier: bezier, meters: meters, circle: circle,
    PIN: PIN, FLY: FLY, T: T
  };
})(typeof window !== 'undefined' ? window : this);
